<template>
  <a-modal
    :visible="visible"
    :title="`${$t('tresorerie.detailsBordereau')} - ${bordereauData.numero}`"
    width="80%"
    :footer="null"
    @cancel="handleCancel"
  >
    <div v-if="bordereauData">
      <!-- Bordereau Information -->
      <a-descriptions
        :title="$t('tresorerie.informationsDuBordereau')"
        bordered
      >
        <a-descriptions-item :label="$t('tresorerie.numero')">
          {{ bordereauData.numero }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('tresorerie.type')">
          <a-tag :color="bordereauType === 'CHEQUE' ? 'blue' : 'green'">
            <a-icon
              :type="bordereauType === 'CHEQUE' ? 'bank' : 'file-text'"
              style="margin-right: 4px"
            />
            {{
              bordereauType === "CHEQUE"
                ? $t("tresorerie.cheques")
                : $t("tresorerie.traites")
            }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item :label="$t('tresorerie.statut')">
          <a-tag :color="getChequeStatutColor(bordereauData.status)">
            {{ $t("status." + bordereauData.status) }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item :label="$t('tresorerie.statutDepot')">
          <a-tag :color="getStatutDepotColor(bordereauData.statutDepot)">
            {{ $t("status." + bordereauData.statutDepot) }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item :label="$t('tresorerie.caisse')">
          {{
            bordereauData.caisseBank
              ? bordereauData.caisseBank.name +
                " (" +
                bordereauData.caisseBank.reference +
                ")"
              : "--"
          }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('tresorerie.dateCreation')">
          {{ moment(bordereauData.createdAt).format("DD/MM/YYYY HH:mm") }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('tresorerie.dateDepotPrevue')">
          {{
            bordereauData.dateDepotPrevue
              ? moment(bordereauData.dateDepotPrevue).format("DD/MM/YYYY")
              : "--"
          }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('tresorerie.dateDepotEffective')">
          {{
            bordereauData.dateDepotEffective
              ? moment(bordereauData.dateDepotEffective).format("DD/MM/YYYY")
              : "--"
          }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('tresorerie.description')" span="3">
          {{ bordereauData.description || $t("tresorerie.aucuneDescription") }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('recette.doneBy')" span="3">
          {{
            bordereauData.createdBy ? bordereauData.createdBy.userName : "--"
          }}
        </a-descriptions-item>
      </a-descriptions>

      <!-- Statistics -->
      <a-divider />
      <a-row :gutter="16" style="margin-bottom: 16px">
        <a-col :span="6">
          <a-statistic
            :title="`${$t('tresorerie.total')} ${typeLabel}`"
            :value="bordereauData.nombreCheques"
            :value-style="{ color: '#1890ff' }"
          />
        </a-col>
        <a-col :span="6">
          <a-statistic
            :title="`${typeLabel} ${$t('tresorerie.valides')}`"
            :value="bordereauData.nombreChequesValides"
            :value-style="{ color: '#52c41a' }"
          />
        </a-col>
        <a-col :span="6">
          <a-statistic
            :title="`${typeLabel} ${$t('tresorerie.enAttente')}`"
            :value="bordereauData.nombreChequesEnAttente"
            :value-style="{ color: '#faad14' }"
          />
        </a-col>
        <a-col :span="6">
          <a-statistic
            :title="$t('tresorerie.montantTotal')"
            :value="formatAmount(bordereauData.montantTotal)"
            :value-style="{ color: '#722ed1' }"
          />
        </a-col>
      </a-row>

      <!-- Cheques/Traites List -->
      <a-divider />
      <h4>{{ $t("tresorerie.listeDes") }} {{ typeLabel }}</h4>
      <a-table
        :columns="dynamicColumns"
        :data-source="bordereauData.chequesDetails || []"
        :pagination="false"
        size="small"
        :row-class-name="getRowClassName"
      >
        <template slot="num" slot-scope="text">
          <span class="numero-cell">
            <a-icon
              :type="bordereauType === 'CHEQUE' ? 'credit-card' : 'file-text'"
              class="action-icon text-primary"
            />
            {{ text }}
          </span>
        </template>
        <template slot="montant" slot-scope="text">
          <span class="amount-cell">
            {{ formatAmount(text) }}
          </span>
        </template>
        <template slot="bankStatus" slot-scope="text">
          <a-tag :color="getChequeStatutColor(text)">
            {{ $t("status." + text) }}
          </a-tag>
        </template>
        <template slot="bank" slot-scope="text">
          {{ text && text.includes("-") ? text.split("-")[0] : text || "--" }}
        </template>
        <template slot="titulaire" slot-scope="text">
          {{ text || "--" }}
        </template>
        <template slot="rib" slot-scope="text">
          {{ text || "--" }}
        </template>
        <template slot="dateEchance" slot-scope="text">
          {{ text ? moment(text).format("DD/MM/YYYY") : "--" }}
        </template>
      </a-table>
    </div>

    <!-- Action Buttons -->
    <div style="text-align: right; margin-top: 24px">
      <a-button
        icon="file-pdf"
        type="primary"
        @click="generatePdf"
        :loading="printLoading"
        :disabled="printLoading"
        style="margin-right: 8px"
      >
        {{ $t("action.imprimer") }}
      </a-button>
      <a-button @click="handleCancel"> {{ $t("tresorerie.fermer") }} </a-button>
    </div>
  </a-modal>
</template>

<script>
import moment from "moment";
import {
  getChequeStatutColor,
  getStatutDepotColor,
} from "@/utilities/settings";
import { generateSingleBordereauPdf } from "../shared/pdfGenerator";
import { formatCurrency as formatCurrencyUtil, getBuildingCurrency } from "@/utilities/countrySpecific";
export default {
  name: "ViewBordereauDetailsModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    bordereauData: {
      type: Object,
      default: () => ({}),
    },
    settings: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      printLoading: false,
      currency: null,
    };
  },
  created() {
    this.currency = getBuildingCurrency();
  },
  computed: {
    bordereauType() {
      return this.bordereauData?.type || "CHEQUE";
    },
    typeLabel() {
      return this.bordereauType === "CHEQUE"
        ? this.$t("tresorerie.cheques")
        : this.$t("tresorerie.traites");
    },
    // Dynamic columns based on bordereau type
    dynamicColumns() {
      const baseColumns = [
        {
          title: this.$t("tresorerie.numero"),
          dataIndex: "num",
          key: "num",
          scopedSlots: { customRender: "num" },
          width: 120,
        },
        {
          title: this.$t("tresorerie.montant"),
          dataIndex: "montant",
          key: "montant",
          scopedSlots: { customRender: "montant" },
          width: 120,
        },
        {
          title: this.$t("tresorerie.banque"),
          dataIndex: "bank",
          key: "bank",
          scopedSlots: { customRender: "bank" },
          width: 120,
        },
      ];

      // Add appropriate column based on bordereau type
      if (this.bordereauType === "CHEQUE") {
        baseColumns.push({
          title: this.$t("tresorerie.titulaire"),
          dataIndex: "titulaire",
          key: "titulaire",
          scopedSlots: { customRender: "titulaire" },
          width: 150,
        });

        // Add dateEcheance column for CHEQUE type
        baseColumns.push({
          title: this.$t("recette.dateEcheance"),
          dataIndex: "dateEchance",
          key: "dateEchance",
          scopedSlots: { customRender: "dateEchance" },
          width: 120,
        });
      } else {
        // For TRAITE, show RIB column
        baseColumns.push({
          title: "RIB",
          dataIndex: "rib",
          key: "rib",
          scopedSlots: { customRender: "rib" },
          width: 150,
        });
      }

      // Add remaining columns
      baseColumns.push({
        title: this.$t("tresorerie.statutBanque"),
        dataIndex: "bankStatus",
        key: "bankStatus",
        scopedSlots: { customRender: "bankStatus" },
        width: 100,
      });

      return baseColumns;
    },
  },
  methods: {
    getChequeStatutColor,

    getStatutDepotColor,

    formatAmount(amount) {
      return formatCurrencyUtil(amount, this.currency);
    },

    getRowClassName(record, index) {
      return index % 2 === 0 ? "table-row-even" : "table-row-odd";
    },

    handleCancel() {
      this.$emit("close");
    },

    moment,

    async generatePdf() {
      this.printLoading = true;
      try {
        const result = generateSingleBordereauPdf(
          this.bordereauData,
          this.settings,
          this.user,
          this.$t.bind(this),
          this.$i18n.locale
        );
        if (result.success) {
          this.$message.success(result.message);
        } else {
          this.$message.error(result.message);
        }
      } catch (error) {
        this.$message.error(this.$t("tresorerie.erreurGenerationBordereau"));
        console.error(error);
      } finally {
        this.printLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.ant-descriptions-item-label {
  font-weight: 600;
}

.ant-divider {
  margin: 16px 0;
}

h4 {
  margin-bottom: 16px;
  color: #1890ff;
}
</style>
